import { useEffect } from "react";

/**
 * Imperative per-route SEO hook. Updates document.title, meta description,
 * canonical, and OG/Twitter tags. Restores the previous values on unmount so
 * routes don't leak metadata into each other.
 *
 * Usage:
 *   useSeo({
 *     title: "Hall of Fame — TorHub",
 *     description: "Past Users of the Week ranked by karma earned…",
 *     path: "/hall-of-fame",
 *   });
 */
export default function useSeo({ title, description, path, image, type = "website" } = {}) {
  useEffect(() => {
    if (!title && !description) return undefined;

    const prevTitle = document.title;
    const setMeta = (selector, attr, value) => {
      if (!value) return null;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (created) {
        el = document.createElement("meta");
        const [, key, name] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
        if (key && name) el.setAttribute(key, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, attr, prev, created };
    };

    if (title) document.title = title;

    const restorers = [];
    const description_ = description;
    restorers.push(setMeta('meta[name="description"]', "content", description_));
    restorers.push(setMeta('meta[property="og:title"]', "content", title));
    restorers.push(setMeta('meta[property="og:description"]', "content", description_));
    restorers.push(setMeta('meta[property="og:type"]', "content", type));
    restorers.push(setMeta('meta[name="twitter:title"]', "content", title));
    restorers.push(setMeta('meta[name="twitter:description"]', "content", description_));
    if (image) {
      restorers.push(setMeta('meta[property="og:image"]', "content", image));
      restorers.push(setMeta('meta[name="twitter:image"]', "content", image));
    }

    // Canonical
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonicalEl;
    let prevHref = null;
    if (path) {
      const origin = window.location.origin;
      const href = `${origin}${path}`;
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      } else {
        prevHref = canonicalEl.getAttribute("href");
      }
      canonicalEl.setAttribute("href", href);
      restorers.push(setMeta('meta[property="og:url"]', "content", href));
    }

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => {
        if (!r) return;
        if (r.created) r.el.remove();
        else r.el.setAttribute(r.attr, r.prev || "");
      });
      if (canonicalEl) {
        if (canonicalCreated) canonicalEl.remove();
        else if (prevHref !== null) canonicalEl.setAttribute("href", prevHref);
      }
    };
  }, [title, description, path, image, type]);
}
